import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Target, 
  Clock, 
  BarChart3, 
  Sparkles, 
  ChevronRight,
  CheckCircle2,
  Brain,
  Lightbulb,
  ArrowRight
} from 'lucide-react';

interface DiagnosticOnboardingProps {
  onComplete: () => void;
}

const onboardingSteps = [
  {
    id: 1,
    icon: Target,
    title: '¿Qué es el Diagnóstico?',
    description: 'Una evaluación diseñada para medir tu nivel actual de competencias de liderazgo y personalizar tu experiencia de aprendizaje.',
    highlight: 'Personalizado para ti',
    color: 'from-primary to-primary/60'
  },
  {
    id: 2,
    icon: Clock,
    title: '¿Cuánto tiempo toma?',
    description: 'El diagnóstico consta de 10 preguntas de opción múltiple. Tomarás aproximadamente 5-10 minutos en completarlo.',
    highlight: '5-10 minutos',
    color: 'from-accent to-accent/60'
  },
  {
    id: 3,
    icon: BarChart3,
    title: '¿Qué obtendré?',
    description: 'Recibirás tu nivel de liderazgo (Básico, Intermedio o Avanzado) y contenido personalizado basado en tus resultados.',
    highlight: 'Contenido adaptado',
    color: 'from-success to-success/60'
  },
  {
    id: 4,
    icon: Sparkles,
    title: '¡Comencemos!',
    description: 'Responde honestamente para obtener los mejores resultados. No hay respuestas correctas o incorrectas.',
    highlight: 'Sé auténtico',
    color: 'from-warning to-warning/60'
  }
];

const benefits = [
  { icon: Brain, text: 'Autoconocimiento de tus habilidades' },
  { icon: Lightbulb, text: 'Ruta de aprendizaje personalizada' },
  { icon: BarChart3, text: 'Seguimiento de tu progreso' }
];

export default function DiagnosticOnboarding({ onComplete }: DiagnosticOnboardingProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setIsExiting(true);
      setTimeout(() => {
        onComplete();
      }, 300);
    }
  };

  const handleSkip = () => {
    setIsExiting(true);
    setTimeout(() => {
      onComplete();
    }, 300);
  };

  const step = onboardingSteps[currentStep];
  const StepIcon = step.icon;
  const isLastStep = currentStep === onboardingSteps.length - 1;

  return (
    <div className={`min-h-screen bg-background p-6 flex items-center justify-center transition-opacity duration-300 ${isExiting ? 'opacity-0' : 'opacity-100'}`}>
      <div className="max-w-lg w-full space-y-8">
        {/* Progress dots */}
        <div className="flex justify-center gap-2">
          {onboardingSteps.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentStep(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentStep 
                  ? 'w-8 bg-primary' 
                  : index < currentStep 
                    ? 'w-2 bg-primary/60' 
                    : 'w-2 bg-muted-foreground/30'
              }`}
            />
          ))}
        </div>

        {/* Main card with animation */}
        <Card 
          key={currentStep}
          className="glass-card border-border/50 overflow-hidden animate-in fade-in slide-in-from-right-4 duration-500"
        >
          <CardContent className="pt-8 pb-8 space-y-6">
            {/* Icon with gradient background */}
            <div className="flex justify-center">
              <div className={`relative w-24 h-24 rounded-2xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg transform transition-transform hover:scale-105`}>
                <StepIcon className="w-12 h-12 text-white" />
                <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-white/20 to-transparent opacity-50" />
              </div>
            </div>

            {/* Content */}
            <div className="text-center space-y-3">
              <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                Paso {step.id} de {onboardingSteps.length}
              </span>
              <h2 className="text-2xl font-bold text-foreground">{step.title}</h2>
              <p className="text-muted-foreground leading-relaxed">{step.description}</p>
              <div className="pt-2">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/50 text-sm font-medium text-foreground">
                  <CheckCircle2 className="w-4 h-4 text-success" />
                  {step.highlight}
                </span>
              </div>
            </div>

            {/* Benefits on last step */}
            {isLastStep && (
              <div className="space-y-3 pt-4 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-200">
                <p className="text-sm text-center text-muted-foreground">Al completar el diagnóstico obtendrás:</p>
                <div className="grid gap-2">
                  {benefits.map((benefit, index) => {
                    const BenefitIcon = benefit.icon;
                    return (
                      <div 
                        key={index}
                        className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border/50"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <BenefitIcon className="w-4 h-4 text-primary" />
                        </div>
                        <span className="text-sm text-foreground">{benefit.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation buttons */}
        <div className="flex gap-3">
          {!isLastStep && (
            <Button 
              variant="ghost" 
              onClick={handleSkip}
              className="flex-1 text-muted-foreground hover:text-foreground"
            >
              Saltar
            </Button>
          )}
          <Button 
            onClick={handleNext}
            className={`gradient-primary ${isLastStep ? 'w-full' : 'flex-1'}`}
          >
            {isLastStep ? (
              <>
                Comenzar Diagnóstico
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            ) : (
              <>
                Siguiente
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </Button>
        </div>

        {/* Skip hint */}
        {!isLastStep && (
          <p className="text-center text-xs text-muted-foreground/60">
            Presiona las teclas ← → para navegar
          </p>
        )}
      </div>
    </div>
  );
}
